import { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import axiosInstance from "../../axiosInstance";
import CustomerAsyncSelect from "./CustomerAsyncSelect";
import { StatusChip } from "./ScanResultTable";
import { resultMeta } from "./scanResults";

export const UNCLAIMED_CUSTOMER_CODE = "ZTO-VH00";

// Menu chọn KH nằm trong bảng/modal: đưa ra body để không bị cắt.
const selectPortalProps = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  styles: { menuPortal: (base) => ({ ...base, zIndex: 2000 }) },
};

/**
 * Xác nhận quét nhập kho VN.
 * Tab 1: đơn có khách hàng (chỉ xem). Tab 2: đơn vô chủ ZTO-VH00 + mã chưa tồn tại của phiên —
 * tích dòng để gán KH (đơn ZTO-VH00) hoặc tạo đơn (mã chưa tồn tại; không chọn KH = ZTO-VH00).
 */
const ScanInConfirmModal = ({ show, onHide, sessionId, pendingLogs, canCreate, canEdit, confirming, onConfirm }) => {
  const [tab, setTab] = useState("claimed");
  const [unknownCodes, setUnknownCodes] = useState([]);
  const [loadingUnknown, setLoadingUnknown] = useState(false);
  const [checked, setChecked] = useState({});
  const [customers, setCustomers] = useState({});
  const [bulkCustomer, setBulkCustomer] = useState(null);

  const claimedLogs = useMemo(
    () => pendingLogs.filter((log) => log.orderCustomerCode !== UNCLAIMED_CUSTOMER_CODE),
    [pendingLogs]
  );
  const unclaimedRows = useMemo(
    () => [
      ...pendingLogs
        .filter((log) => log.orderCustomerCode === UNCLAIMED_CUSTOMER_CODE)
        .map((log) => ({ code: log.code, kind: "UNCLAIMED", log })),
      ...unknownCodes.map((code) => ({ code, kind: "NEW" })),
    ],
    [pendingLogs, unknownCodes]
  );

  const canSelect = (row) => (row.kind === "NEW" ? canCreate : canEdit);
  const selectableRows = unclaimedRows.filter(canSelect);
  const checkedRows = unclaimedRows.filter((row) => checked[row.code]);
  const allChecked = selectableRows.length > 0 && selectableRows.every((row) => checked[row.code]);

  // Mở modal: làm mới lựa chọn + tải mã chưa tồn tại của phiên (chỉ khi có quyền tạo đơn).
  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    setChecked({});
    setCustomers({});
    setBulkCustomer(null);
    setUnknownCodes([]);
    const hasUnclaimed = pendingLogs.some((log) => log.orderCustomerCode === UNCLAIMED_CUSTOMER_CODE);
    setTab(hasUnclaimed ? "unclaimed" : "claimed");
    if (!canCreate || !sessionId) return undefined;
    setLoadingUnknown(true);
    axiosInstance
      .get(`/api/warehouse/sessions/${sessionId}/unknownCodes`)
      .then((res) => {
        if (cancelled) return;
        const codes = res.data?.data || [];
        setUnknownCodes(codes);
        if (codes.length > 0) setTab("unclaimed");
      })
      .catch(() => {
        if (!cancelled) setUnknownCodes([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingUnknown(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, sessionId, canCreate]);

  const toggleRow = (code) => setChecked((prev) => ({ ...prev, [code]: !prev[code] }));
  const toggleAll = () =>
    setChecked(allChecked ? {} : Object.fromEntries(selectableRows.map((row) => [row.code, true])));
  // Chọn KH ở dòng = muốn xử lý dòng đó → tự tích (bỏ KH không tự bỏ tích).
  const setRowCustomer = (code, option) => {
    setCustomers((prev) => ({ ...prev, [code]: option }));
    if (option) setChecked((prev) => ({ ...prev, [code]: true }));
  };
  const applyBulkCustomer = () =>
    setCustomers((prev) => ({ ...prev, ...Object.fromEntries(checkedRows.map((row) => [row.code, bulkCustomer])) }));

  const assignments = checkedRows
    .filter((row) => row.kind === "UNCLAIMED" && customers[row.code]?.value)
    .map((row) => ({ code: row.code, customerCode: customers[row.code].value }));
  const createOrders = checkedRows
    .filter((row) => row.kind === "NEW")
    .map((row) => ({ code: row.code, customerCode: customers[row.code]?.value || "" }));
  const nothingToDo = pendingLogs.length === 0 && createOrders.length === 0;

  const submit = () => onConfirm({ assignments, createOrders });

  return (
    <Modal show={show} onHide={() => !confirming && onHide()} size="xl" scrollable className="warehouse-modal">
      <Modal.Header closeButton={!confirming}>
        <Modal.Title as="h5">Xác nhận nhập kho VN</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button type="button" className={`nav-link ${tab === "claimed" ? "active" : ""}`} onClick={() => setTab("claimed")}>
              Đơn có khách hàng ({claimedLogs.length})
            </button>
          </li>
          <li className="nav-item">
            <button type="button" className={`nav-link ${tab === "unclaimed" ? "active" : ""}`} onClick={() => setTab("unclaimed")}>
              Đơn vô chủ ({loadingUnknown ? "…" : unclaimedRows.length})
            </button>
          </li>
        </ul>

        {tab === "claimed" && (
          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>Mã vận đơn</th>
                  <th>Đơn hàng</th>
                  <th>Trạng thái quét</th>
                  <th>Trạng thái hiện tại</th>
                  <th>Mã khách hàng</th>
                </tr>
              </thead>
              <tbody>
                {claimedLogs.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-3">Không có đơn nào</td>
                  </tr>
                )}
                {claimedLogs.map((log, index) => {
                  const meta = resultMeta(log.result);
                  return (
                    <tr key={log.id || log.code}>
                      <td>{index + 1}</td>
                      <td className="fw-semibold">{log.code}</td>
                      <td>{log.orderCode || "-"}</td>
                      <td><span className={`badge bg-${meta.variant}`}>{meta.label}</span></td>
                      <td><StatusChip name={log.statusBeforeName} color={log.statusBeforeColor} /></td>
                      <td>
                        {log.orderCustomerCode || "-"}
                        {log.orderCustomerName && <div className="small text-muted">{log.orderCustomerName}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {tab === "unclaimed" && (
          <>
            <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
              <div style={{ minWidth: 280, flex: "1 1 280px", maxWidth: 420 }}>
                <CustomerAsyncSelect
                  value={bulkCustomer}
                  onChange={setBulkCustomer}
                  isDisabled={confirming || checkedRows.length === 0}
                  isClearable
                  {...selectPortalProps}
                />
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={applyBulkCustomer}
                disabled={confirming || checkedRows.length === 0 || !bulkCustomer}
              >
                Áp dụng cho {checkedRows.length} dòng đã tích
              </button>
            </div>
            <div className="table-responsive">
              <table className="table table-sm table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 36 }}>
                      <input
                        type="checkbox"
                        checked={allChecked}
                        onChange={toggleAll}
                        disabled={confirming || selectableRows.length === 0}
                        aria-label="Chọn tất cả"
                      />
                    </th>
                    <th>Mã vận đơn</th>
                    <th>Loại</th>
                    <th>Đơn hàng</th>
                    <th style={{ minWidth: 280 }}>Gán mã khách hàng</th>
                  </tr>
                </thead>
                <tbody>
                  {!loadingUnknown && unclaimedRows.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-3">Không có đơn vô chủ / mã chưa tồn tại</td>
                    </tr>
                  )}
                  {unclaimedRows.map((row) => {
                    const selectable = canSelect(row);
                    const isChecked = Boolean(checked[row.code]);
                    return (
                      <tr key={`${row.kind}-${row.code}`}>
                        <td>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleRow(row.code)}
                            disabled={confirming || !selectable}
                            title={selectable ? undefined : "Bạn không có quyền xử lý mã này"}
                          />
                        </td>
                        <td className="fw-semibold">{row.code}</td>
                        <td>
                          {row.kind === "NEW" ? (
                            <span className="badge bg-secondary">Chưa tồn tại</span>
                          ) : (
                            <span className="badge bg-warning text-dark">{UNCLAIMED_CUSTOMER_CODE}</span>
                          )}
                        </td>
                        <td>
                          {row.log?.orderCode || "-"}
                          {row.log?.statusBeforeName && (
                            <div><StatusChip name={row.log.statusBeforeName} color={row.log.statusBeforeColor} /></div>
                          )}
                        </td>
                        <td>
                          <CustomerAsyncSelect
                            value={customers[row.code] || null}
                            onChange={(option) => setRowCustomer(row.code, option)}
                            isDisabled={confirming || !selectable}
                            isClearable
                            {...selectPortalProps}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <div className="small">
          Nhập kho <b>{pendingLogs.length}</b> đơn · gán KH <b>{assignments.length}</b> đơn vô chủ · tạo mới <b>{createOrders.length}</b> đơn
        </div>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-sm btn-secondary" onClick={onHide} disabled={confirming}>
            Hủy
          </button>
          <button type="button" className="btn btn-sm btn-success fw-bold" onClick={submit} disabled={confirming || nothingToDo}>
            {confirming ? "Đang xác nhận..." : "Xác nhận nhập kho"}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ScanInConfirmModal;
